import type { ParsedItem } from '../types';

/**
 * IKEA text parser - extracts product information from pasted IKEA shopping list text
 * Ported from the browser extension
 */
export function parseIKEAText(content: string): ParsedItem[] {
  const items: ParsedItem[] = [];
  const lines = content.split('\n').map(line => line.trim()).filter(line => line);
  const productCodeLines: Map<string, number> = new Map();

  // First pass: find all product codes
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const codeMatch = line.match(/(\d{3}\.\d{3}\.\d{2})/);
    if (codeMatch) {
      const code = codeMatch[1];
      if (/^\d{3}\.\d{3}\.\d{2}$/.test(code) && !productCodeLines.has(code)) {
        productCodeLines.set(code, i);
      }
    }
  }

  const isBrandLine = (line: string): boolean => {
    if (!line?.trim()) return false;
    const s = line.trim();
    if (!/[A-Z]/.test(s)) return false;
    if (/[a-z]/.test(s)) return false;
    const alphaCount = (s.match(/[A-Za-z]/g) || []).length;
    if (alphaCount < s.length * 0.5) return false;
    if (s.length < 3 || s.length > 30) return false;
    return true;
  };

  const isDescriptionLine = (line: string): boolean => {
    if (!line?.trim()) return false;
    const s = line.trim();
    if (!/[a-z]/.test(s)) return false;
    if (s.length < 5) return false;
    return true;
  };

  const hasProductCode = (line: string): boolean => /\d{3}\.\d{3}\.\d{2}/.test(line);

  const extractQuantityFromNextLine = (codeLineIdx: number): number => {
    const nextLine = lines[codeLineIdx + 1] || '';
    const match = nextLine.match(/^(\d{1,2})\s*x\s/i);
    if (match) {
      const qty = parseInt(match[1], 10);
      if (qty >= 1 && qty <= 99) return qty;
    }
    return 1;
  };

  const extractPriceFromPrevLine = (codeLineIdx: number): number | undefined => {
    const prevLine = lines[codeLineIdx - 1] || '';
    const match = prevLine.match(/^\$\s*(\d+(?:,\d{3})*(?:\.\d{2})?)$/);
    if (match) {
      try { return parseFloat(match[1].replace(/,/g, '')); } catch { /* ignore */ }
    }
    const inlineMatch = prevLine.match(/\$\s*(\d+(?:,\d{3})*(?:\.\d{2})?)/);
    if (inlineMatch) {
      try { return parseFloat(inlineMatch[1].replace(/,/g, '')); } catch { /* ignore */ }
    }
    return undefined;
  };

  const collectBrandAndDescription = (codeLineIdx: number): { brand: string | null; description: string | null } => {
    let brand: string | null = null;
    const descriptionParts: string[] = [];

    for (let i = codeLineIdx - 1; i >= Math.max(0, codeLineIdx - 10); i--) {
      const ln = lines[i]?.trim() || '';
      if (!ln) continue;
      if (hasProductCode(ln)) break;
      if (isBrandLine(ln)) {
        brand = ln;
        break;
      }
      if (isDescriptionLine(ln)) {
        descriptionParts.unshift(ln);
      }
    }

    return { brand, description: descriptionParts.length > 0 ? descriptionParts.join(' ') : null };
  };

  const cleanDescriptionText = (text: string): string => {
    if (!text) return '';
    let cleaned = text.replace(/\d{3}\.\d{3}\.\d{2}/g, '');
    cleaned = cleaned.replace(/\d+\s*[×x]\s*/gi, '');
    cleaned = cleaned.replace(/\$[\d,]+\.\d{2}/g, '');
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    cleaned = cleaned.replace(/[.,;:-]+$/, '');
    if (cleaned && cleaned[0] && /[a-z]/.test(cleaned[0])) {
      cleaned = cleaned[0].toUpperCase() + cleaned.slice(1);
    }
    return cleaned;
  };

  // Second pass: extract product information
  for (const [productCode, codeLineNum] of productCodeLines) {
    try {
      const quantity = extractQuantityFromNextLine(codeLineNum);
      const price = extractPriceFromPrevLine(codeLineNum);
      const { brand, description } = collectBrandAndDescription(codeLineNum);

      let itemName = '';
      if (brand && description) {
        itemName = `${brand} ${description}`;
      } else if (brand) {
        itemName = brand;
      } else if (description) {
        itemName = description;
      } else {
        itemName = `IKEA Product ${productCode}`;
      }

      itemName = cleanDescriptionText(itemName);
      if (!itemName || itemName.length < 3) {
        itemName = `IKEA Product ${productCode}`;
      }

      items.push({
        product_code: productCode,
        item_name: itemName,
        quantity_needed: quantity,
        estimated_price: price,
      });
    } catch { continue; }
  }

  return items;
}
